import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

export type Status = 'IDLE' | 'LOADING' | 'ERROR' | 'SUCCESS'

type Product = {
  latest: string
  versions: {
    [version: string]: string
  }
}

export type ProductName =
  | 'access-control'
  | 'agent-dashboard'
  | 'assess'
  | 'audit'
  | 'scan'
  | 'serverless'
  | 'platform'
  | 'scim'

export type Manifest = {
  specs: {
    'access-control': Product
    'agent-dashboard': Product
    assess: Product
    audit: Product
    scan: Product
    serverless: Product
    platform: Product
    scim: Product
  }
}

type HookPayload = {
  getLatestLink: (s: ProductName) => string | null
  getVersionUrl: (s: string) => string
} & (
  | {
      status: 'SUCCESS'
      manifest: Manifest
    }
  | {
      status: 'IDLE' | 'LOADING' | 'ERROR'
      manifest?: null
    }
)

export const SpecManifestContext = createContext<HookPayload>({
  status: 'IDLE',
  manifest: null,
  getLatestLink: () => '',
  getVersionUrl: () => ''
})

export const SpecManifestContextProvider = ({ children }: { children?: ReactNode }) => {
  const [status, setStatus] = useState<Status>('IDLE')
  const [manifest, setManifest] = useState<Manifest | null>(null)
  // const specUrl = getSpecUrl()

  const fetchManifest = useCallback(async () => {
    setStatus('LOADING')

    try {
      const resp = await fetch(`/docs/manifest.json`)
      if (resp.ok) {
        setManifest((await resp.json()) as Manifest)
        setStatus('SUCCESS')
      } else {
        setStatus('ERROR')
      }
    } catch (e) {
      console.error(e)
      setStatus('ERROR')
    }
  }, [])

  useEffect(() => {
    fetchManifest()
  }, [fetchManifest])

  const getLatestLink = (productName: ProductName) =>
    manifest?.specs[productName] ? `/${productName}/${manifest.specs[productName]?.latest}` : null

  const getVersionUrl = (route: string) => {
    const [productName, version] = route.split('/') as [ProductName, string]
    return manifest ? `/docs/${manifest.specs[productName].versions[version]}` : ''
  }

  return (
    <SpecManifestContext.Provider
      value={
        {
          status,
          manifest,
          getLatestLink,
          getVersionUrl
        } as HookPayload
      }
    >
      {children}
    </SpecManifestContext.Provider>
  )
}

export const useSpecManifest = () => useContext(SpecManifestContext)
